import { useState, useCallback, useEffect } from 'react';
import { meetingsService } from '@/services/meetingsService';

export function useCalendarIntegrations(userId: string | undefined) {
  const [calendarIntegrations, setCalendarIntegrations] = useState<{
    google?: boolean;
    microsoft?: boolean;
    apple?: boolean;
  }>({});
  const [loading, setLoading] = useState(true);

  // Fetch calendar integrations for the current user
  const fetchCalendarIntegrations = useCallback(async () => {
    if (!userId) {
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      const data = await meetingsService.getCalendarIntegrations();
      
      const integrations: Record<string, boolean> = {};
      if (Array.isArray(data)) {
        data.forEach((item: any) => {
          integrations[item.provider] = true;
        });
      }

      setCalendarIntegrations(integrations);
    } catch (error) {
      console.error('Failed to fetch calendar integrations', error);
    } finally {
      setLoading(false);
    }
  }, [userId]);

  // Update calendar integration status
  const updateCalendarIntegration = async (
    provider: 'google' | 'microsoft' | 'apple',
    connected: boolean
  ) => {
    if (!userId) return;

    try {
      if (connected) {
        // Connect calendar integration
        await meetingsService.connectCalendar({ provider });

        setCalendarIntegrations({
          ...calendarIntegrations,
          [provider]: true,
        });
      } else {
        // Disconnect calendar integration
        // Find the integration ID first
        const integrations = await meetingsService.getCalendarIntegrations();
        const integration = Array.isArray(integrations) 
          ? integrations.find((i: any) => i.provider === provider)
          : null;
        
        if (integration?.id) {
          await meetingsService.disconnectCalendar(integration.id);
        }

        const updatedIntegrations = { ...calendarIntegrations };
        delete updatedIntegrations[provider];
        setCalendarIntegrations(updatedIntegrations);
      }
    } catch (error) {
      console.error(
        `Failed to ${
          connected ? 'connect' : 'disconnect'
        } ${provider} calendar`,
        error
      );
      throw error;
    }
  };

  // Load integrations on mount
  useEffect(() => {
    fetchCalendarIntegrations();
  }, [fetchCalendarIntegrations]);

  return {
    calendarIntegrations,
    loading,
    fetchCalendarIntegrations,
    updateCalendarIntegration,
  };
}
